/**
 * Texto do WhatsApp por report type — espelha `emailTemplates.ts`.
 *
 * Cada tool define seu `email_template` em TABLE_DATA (mesmo campo usado
 * pelo e-mail); `WHATSAPP_BUILDERS` dispatcha por essa chave. Sem builder
 * registrado, cai no builder de HD (comportamento histórico, preservado
 * para os tools que já usavam texto de WhatsApp antes deste dispatch
 * existir — HDs, OS em aberto, cross-reference).
 */

import type { TableData } from '../types/tableData'

interface HdDetail {
  historico?: { registrado_por?: string; data?: string; observacao?: string }[]
  [key: string]: unknown
}

function getLastHistorico(detail: HdDetail | undefined): string {
  if (!detail?.historico?.length) return ''
  const entries = detail.historico.filter(
    h => h.registrado_por !== 'Registrado por' && h.observacao !== 'Observação'
  )
  if (entries.length === 0) return ''
  const last = entries[entries.length - 1]
  return last?.observacao?.trim() || ''
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Bom dia'
  if (hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

function buildHdWhatsAppMessage(data: TableData): string {
  const lines: string[] = []

  lines.push(`${getGreeting()}!\nSegue resumo dos HDs em aberto:`)
  lines.push('')

  const rows = data.rows
  if (rows.length === 0) {
    lines.push('Nenhum HD em aberto no periodo.')
    return lines.join('\n')
  }

  for (const row of rows) {
    const numero = row.numero ?? ''
    const os = row.numero_os ?? ''
    const chassi = row.chassi ?? ''
    const dias = Number(row.dias_parados) || 0
    const detail = row.detail as HdDetail | undefined
    const lastUpdate = getLastHistorico(detail)

    let header = `*HD: ${numero}*`
    if (dias > 0) header += ` (${dias} dias)`
    lines.push(header)

    const identifiers: string[] = []
    if (os) identifiers.push(`OS: ${os}`)
    if (chassi) identifiers.push(`Chassi: ${chassi}`)
    if (identifiers.length > 0) {
      lines.push(identifiers.join(' | '))
    }

    // Linha CSPS: aparece quando a feature `include_csps` está ativa e algum
    // dos 4 campos foi hidratado (vínculo HD↔Pedido). Adicionada SEM remover
    // o quote do último comentário — `> ...` continua intacto abaixo.
    const pedidoCsps = String(row.pedido_csps ?? '').trim()
    const dataPedidoCsps = String(row.data_pedido_csps ?? '').trim()
    const nf = String(row.numero_nota_fiscal ?? '').trim()
    const prev = String(row.data_entrega_prevista ?? '').trim()
    if (pedidoCsps || dataPedidoCsps || nf || prev) {
      const parts: string[] = []
      if (pedidoCsps) {
        parts.push(
          dataPedidoCsps
            ? `Pedido CSPS: ${pedidoCsps} (${dataPedidoCsps})`
            : `Pedido CSPS: ${pedidoCsps}`
        )
      } else if (dataPedidoCsps) {
        parts.push(`Data pedido: ${dataPedidoCsps}`)
      }
      if (nf) parts.push(`NF: ${nf}`)
      if (prev) parts.push(`Prev. entrega: ${prev}`)
      if (parts.length > 0) lines.push(`📦 ${parts.join(' | ')}`)
    }

    if (lastUpdate) {
      // '> ' no início de cada linha renderiza como citação no WhatsApp.
      const quoted = lastUpdate
        .split('\n')
        .map(l => `> ${l}`)
        .join('\n')
      lines.push(quoted)
    }

    lines.push('')
  }

  if (data.summary) {
    const { total, avg_days } = data.summary
    const parts = [`*Total: ${total} HDs*`]
    if (avg_days !== undefined && avg_days !== null) {
      parts.push(`Média: ${avg_days}d parado`)
    }
    lines.push(parts.join(' | '))
  }

  return lines.join('\n')
}

/**
 * Builder para `check_part_availability` (Conferência de Peça — General Part
 * Inquiry). Dois formatos de TABLE_DATA convivem:
 *
 * - **Antigo** (sem `origem` nas rows): todas as rows são depósitos do PN
 *   consultado e compartilham os campos de supersession/bloqueio.
 * - **Novo** (change `csps-part-supersession-fix` no back): rows de mais de um
 *   PN (o consultado e os seguidos na cadeia de substituição) e de duas
 *   origens, `Fábrica (CSPS)` e `MDi (SGE)`. Listar tudo como depósito, como o
 *   formato antigo faz, misturaria o estoque do PN antigo com o do novo.
 */
function buildCheckPartAvailabilityWhatsAppMessage(data: TableData): string {
  const lines: string[] = []
  const partNumber = (
    data.subtitle?.replace(/^Part number\s*/i, '').trim() || ''
  ).toUpperCase()

  lines.push(`${getGreeting()}!`)
  lines.push(`Conferência de peça — PN *${partNumber}*:`)
  lines.push('')

  if (data.rows.length === 0) {
    lines.push('Nenhum dado retornado para esse part number.')
    return lines.join('\n')
  }

  const isChainFormat = data.rows.some(row => 'origem' in row)
  const body = isChainFormat
    ? buildPartChainLines(data.rows, partNumber, data.auditoria)
    : buildPartLegacyLines(data.rows)
  return [...lines, ...body].join('\n')
}

const ORIGEM_FABRICA = 'Fábrica (CSPS)'
const ORIGEM_MDI = 'MDi (SGE)'
const PAPEL_PEDIR = 'pedir'
const PAPEL_EQUIVALENTE = 'equivalente'

type PartRow = Record<string, unknown>

function text(row: PartRow | undefined, key: string): string {
  return String(row?.[key] ?? '').trim()
}

function buildPartLegacyLines(rows: PartRow[]): string[] {
  const lines: string[] = []
  const first = rows[0]
  const blockMessage = text(first, 'block_message')
  const subTo = text(first, 'sub_to')

  if (blockMessage) {
    lines.push(`*Bloqueio:* ${blockMessage}`)
    lines.push('')
  }
  if (subTo) {
    lines.push(`*Substituído por:* ${subTo}`)
    lines.push('')
  }

  lines.push('*Disponibilidade por depósito:*')
  lines.push(...depositLines(rows))
  return lines
}

function depositLines(rows: PartRow[]): string[] {
  return rows
    .filter(row => {
      const deposito = text(row, 'deposito')
      return deposito && deposito !== '-'
    })
    .map(
      row =>
        `• ${text(row, 'deposito')}: ${text(row, 'available_qty') || 'sem estoque'}`
    )
}

function uniquePns(rows: PartRow[]): string[] {
  return [...new Set(rows.map(row => text(row, 'pn')).filter(Boolean))]
}

const ROLE_LABEL: Record<string, string> = {
  pedir: 'pedir este',
  equivalente: 'também serve',
  intermediario: 'intermediário',
  nao_confirmado: 'não confirmado',
  consultado: 'consultado',
}

function buildPartChainLines(
  rows: PartRow[],
  partNumber: string,
  auditoria: Record<string, unknown> | undefined
): string[] {
  const lines: string[] = []
  const factory = rows.filter(row => text(row, 'origem') === ORIGEM_FABRICA)
  const mdi = rows.filter(row => text(row, 'origem') === ORIGEM_MDI)
  const chain = uniquePns(factory)
  const consulted =
    factory.find(row => text(row, 'pn') === partNumber) ?? factory[0]
  const toOrder = factory.find(row => text(row, 'papel_codigo') === PAPEL_PEDIR)
  const pnToOrder = text(toOrder, 'pn')

  const isReversible = text(consulted, 'papel_codigo') === PAPEL_EQUIVALENTE
  if (pnToOrder && isReversible) {
    lines.push(`*Substituição reversível:* ${partNumber} e ${pnToOrder} servem`)
  } else if (pnToOrder && pnToOrder !== partNumber) {
    lines.push(`*Substituído:* peça o *${pnToOrder}*`)
  } else if (pnToOrder) {
    lines.push('*PN vigente* ✅')
  } else {
    lines.push(
      '*Atenção:* não confirmei o PN vigente. Confira no CSPS antes de pedir.'
    )
  }
  if (chain.length > 1) lines.push(`*Cadeia:* ${chain.join(' → ')}`)

  const status = text(consulted, 'material_status')
  if (status && text(consulted, 'papel_codigo') !== PAPEL_PEDIR) {
    lines.push(`*Status do material:* ${status}`)
  }
  const blockMessage = text(consulted, 'block_message')
  if (blockMessage) lines.push(`*Bloqueio:* ${blockMessage}`)
  lines.push('')

  // Todos os PNs da cadeia, na ordem em que o portal foi seguido: a fábrica de
  // um PN intermediário também é estoque que o vendedor pode querer ver.
  for (const pn of chain) {
    const role = text(
      factory.find(row => text(row, 'pn') === pn),
      'papel_codigo'
    )
    lines.push(`*Fábrica — ${pn} (${ROLE_LABEL[role] ?? 'consultado'}):*`)
    const deposits = depositLines(factory.filter(row => text(row, 'pn') === pn))
    lines.push(...(deposits.length ? deposits : ['• sem depósito listado']))
    lines.push('')
  }

  lines.push('*Na MDi (SGE):*')
  lines.push(...mdiLines(mdi, auditoria))
  return lines
}

/** Estoque da MDi, distinguindo "não consultei", "zerado" e "sem cadastro". */
function mdiLines(
  mdi: PartRow[],
  auditoria: Record<string, unknown> | undefined
): string[] {
  if (auditoria && auditoria.estoque_mdi_consultado === false) {
    return ['• Não consegui consultar o estoque da MDi no SGE agora.']
  }
  const lines = mdi.map(row => {
    const pending = text(row, 'compra_pendente')
    const inPurchase =
      pending && pending !== '0' ? ` + ${pending} em compra` : ''
    return (
      `• ${text(row, 'deposito')}: ${text(row, 'available_qty') || '0'} un.${inPurchase} ` +
      `do ${text(row, 'pn')} (cód. ${text(row, 'codigo_sge')}, ${text(row, 'marca') || 'sem marca'})`
    )
  })
  const withoutStock = Array.isArray(auditoria?.estoque_mdi_sem_saldo)
    ? (auditoria.estoque_mdi_sem_saldo as PartRow[])
    : []
  for (const item of withoutStock) {
    lines.push(
      `• ${text(item, 'pn')}: cadastrado (cód. ${text(item, 'codigo_sge')}) mas sem saldo`
    )
  }
  return lines.length ? lines : ['• Sem cadastro com esses números no SGE.']
}

const WHATSAPP_BUILDERS: Record<string, (data: TableData) => string> = {
  check_part_availability: buildCheckPartAvailabilityWhatsAppMessage,
}

/**
 * Build WhatsApp text for the given table data.
 * Uses `data.email_template` to select the right builder.
 */
export function buildWhatsAppMessage(data: TableData): string {
  const builder = WHATSAPP_BUILDERS[data.email_template ?? '']
  return builder ? builder(data) : buildHdWhatsAppMessage(data)
}
